<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center">
      <span>Volunteers Management</span>
      <div class="d-flex gap-2">
        <v-btn color="secondary" variant="outlined" @click="showEmailModal = true">
          <v-icon start>mdi-email-multiple</v-icon>
          Copy Emails
        </v-btn>
        <v-btn color="primary" @click="downloadCSV" :loading="downloadingCSV">
          <v-icon start>mdi-download</v-icon>
          Download CSV
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text>
      <div class="filters-container mb-4">
        <v-text-field
          v-model="searchQuery"
          label="Search volunteers..."
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          clearable
          hide-details
          class="search-field"
        ></v-text-field>

        <v-select
          v-model="interestFilter"
          label="Filter by interest"
          :items="interestOptions"
          variant="outlined"
          density="compact"
          clearable
          hide-details
          class="interest-filter"
        ></v-select>
      </div>

      <v-data-table
        :headers="headers"
        :items="filteredVolunteers"
        :loading="loading"
        :items-per-page="10"
        class="elevation-1"
        @click:row="openViewModal"
        hover
      >
        <!-- Status Column -->
        <template v-slot:item.status="{ item }">
          <v-chip :color="(item as any).status === 'active' ? 'success' : 'grey'" size="small">
            {{ (item as any).status }}
          </v-chip>
        </template>

        <!-- Phone Column -->
        <template v-slot:item.phone="{ item }">
          <span v-if="(item as any).phone">{{ (item as any).phone }}</span>
          <span v-else class="text-grey">-</span>
        </template>

        <!-- Interests Column -->
        <template v-slot:item.skills="{ item }">
          <div class="skills-container">
            <v-chip
              v-for="interest in (item as any).interested_in"
              :key="interest"
              size="x-small"
              color="primary"
              class="mr-1 mb-1"
              :title="getInterestLabel(interest)"
            >
              {{ getInterestShortLabel(interest) }}
            </v-chip>
            <span v-if="!(item as any).interested_in?.length" class="text-grey">-</span>
          </div>
        </template>

        <!-- Actions Column -->
        <template v-slot:item.actions="{ item }">
          <v-btn
            icon="mdi-pencil"
            size="small"
            variant="text"
            @click.stop="openEditModal(item)"
            title="Edit"
          ></v-btn>
          <!-- <v-btn
            v-if="(item as any).status === 'active'"
            icon="mdi-pause"
            size="small"
            variant="text"
            color="warning"
            @click.stop="deactivateVolunteer(item)"
            title="Deactivate"
          ></v-btn>
          <v-btn
            v-else
            icon="mdi-play"
            size="small"
            variant="text"
            color="success"
            @click.stop="activateVolunteer(item)"
            title="Activate"
          ></v-btn> -->
          <v-btn
            icon="mdi-delete"
            size="small"
            variant="text"
            color="error"
            @click.stop="deleteVolunteer(item)"
            title="Delete"
          ></v-btn>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- View Volunteer Details Modal -->
    <v-dialog v-model="showViewModal" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Volunteer Details</span>
        </v-card-title>

        <v-card-text v-if="viewingVolunteer">
          <v-list>
            <v-list-item>
              <v-list-item-title class="font-weight-bold">Name</v-list-item-title>
              <v-list-item-subtitle>{{ viewingVolunteer.name }}</v-list-item-subtitle>
            </v-list-item>

            <v-list-item>
              <v-list-item-title class="font-weight-bold">Email</v-list-item-title>
              <v-list-item-subtitle>
                <a :href="`mailto:${viewingVolunteer.email}`">{{ viewingVolunteer.email }}</a>
              </v-list-item-subtitle>
            </v-list-item>

            <v-list-item>
              <v-list-item-title class="font-weight-bold">Phone</v-list-item-title>
              <v-list-item-subtitle>{{ viewingVolunteer.phone || 'Not provided' }}</v-list-item-subtitle>
            </v-list-item>

            <v-list-item>
              <v-list-item-title class="font-weight-bold">Status</v-list-item-title>
              <v-list-item-subtitle>
                <v-chip :color="viewingVolunteer.status === 'active' ? 'success' : 'grey'" size="small">
                  {{ viewingVolunteer.status }}
                </v-chip>
              </v-list-item-subtitle>
            </v-list-item>

            <v-divider class="my-3"></v-divider>

            <v-list-item>
              <v-list-item-title class="font-weight-bold mb-2">How They Want to Help</v-list-item-title>
              <v-list-item-subtitle>
                <v-chip
                  v-for="interest in viewingVolunteer.interested_in"
                  :key="interest"
                  size="small"
                  color="primary"
                  class="mr-2 mb-2"
                >
                  <v-icon start size="small">{{ getInterestIcon(interest) }}</v-icon>
                  {{ getInterestLabel(interest) }}
                </v-chip>
                <span v-if="!viewingVolunteer.interested_in?.length" class="text-grey">
                  No interests specified
                </span>
              </v-list-item-subtitle>
            </v-list-item>

            <v-divider class="my-3"></v-divider>

            <v-list-item v-if="viewingVolunteer.comments">
              <v-list-item-title class="font-weight-bold">Comments</v-list-item-title>
              <v-list-item-subtitle class="text-wrap">{{ viewingVolunteer.comments }}</v-list-item-subtitle>
            </v-list-item>

            <v-list-item>
              <v-list-item-title class="font-weight-bold">Signup Date</v-list-item-title>
              <v-list-item-subtitle>{{ formatDate(viewingVolunteer.created_at) }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeViewModal">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Copy Emails Modal -->
    <v-dialog v-model="showEmailModal" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Volunteer Emails</span>
        </v-card-title>

        <v-card-text>
          <p class="mb-3">
            {{ filteredVolunteers.length }} volunteer{{ filteredVolunteers.length !== 1 ? 's' : '' }}
            {{ interestFilter ? `interested in ${getInterestLabel(interestFilter)}` : '' }}
          </p>
          <v-textarea
            v-model="emailsList"
            rows="10"
            readonly
            variant="outlined"
            label="Email addresses (comma-separated)"
            auto-grow
          ></v-textarea>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="showEmailModal = false">Close</v-btn>
          <v-btn color="primary" @click="copyEmails">
            <v-icon start>mdi-content-copy</v-icon>
            Copy to Clipboard
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Volunteer Modal -->
    <v-dialog v-model="showEditModal" max-width="700px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Edit Volunteer</span>
        </v-card-title>

        <v-card-text v-if="editingVolunteer">
          <v-form ref="editForm">
            <v-text-field
              v-model="editingVolunteer.name"
              label="Name"
              variant="outlined"
              density="compact"
              required
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model="editingVolunteer.email"
              label="Email"
              type="email"
              variant="outlined"
              density="compact"
              required
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model="editingVolunteer.phone"
              label="Phone"
              variant="outlined"
              density="compact"
              class="mb-3"
            ></v-text-field>

            <v-select
              v-model="editingVolunteer.interested_in"
              :items="interestOptions"
              label="Interests"
              variant="outlined"
              density="compact"
              multiple
              chips
              closable-chips
              class="mb-3"
            ></v-select>

            <v-select
              v-model="editingVolunteer.status"
              :items="['active', 'inactive']"
              label="Status"
              variant="outlined"
              density="compact"
              class="mb-3"
            ></v-select>

            <v-textarea
              v-model="editingVolunteer.comments"
              label="Comments"
              variant="outlined"
              rows="3"
              auto-grow
            ></v-textarea>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeEditModal">Cancel</v-btn>
          <v-btn color="primary" @click="saveVolunteer" :loading="saving">
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { api } from '@/composables/api'
import type { Volunteer } from '@/types/Volunteer'

const volunteers = ref<Volunteer[]>([])
const loading = ref(false)
const searchQuery = ref('')
const interestFilter = ref('')
const showViewModal = ref(false)
const showEmailModal = ref(false)
const showEditModal = ref(false)
const viewingVolunteer = ref<Volunteer | null>(null)
const editingVolunteer = ref<Volunteer | null>(null)
const downloadingCSV = ref(false)
const saving = ref(false)

const headers = [
  { title: 'Name', key: 'name', sortable: true },
  { title: 'Email', key: 'email', sortable: true },
  { title: 'Phone', key: 'phone', sortable: true },
  { title: 'Interests', key: 'skills', sortable: false },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false }
]

const interestOptions = [
  { title: 'Computer Refurbishment', value: 'computer_refurbishment' },
  { title: 'Distribution/Delivery', value: 'distribution_delivery' },
  { title: 'STEM Mentoring', value: 'stem_mentoring' },
  { title: 'STEM Workshops/Career Days', value: 'stem_workshops' }
]

// Helper functions for interests
const getInterestLabel = (interest: string) => {
  const labels: Record<string, string> = {
    'computer_refurbishment': 'Computer Refurbishment',
    'distribution_delivery': 'Distribution/Delivery',
    'stem_mentoring': 'STEM Mentoring',
    'stem_workshops': 'STEM Workshops/Career Days'
  }
  return labels[interest] || interest
}

const getInterestShortLabel = (interest: string) => {
  const labels: Record<string, string> = {
    'computer_refurbishment': 'Refurbishment',
    'distribution_delivery': 'Delivery',
    'stem_mentoring': 'Mentoring',
    'stem_workshops': 'Workshops'
  }
  return labels[interest] || interest
}

const getInterestIcon = (interest: string) => {
  const icons: Record<string, string> = {
    'computer_refurbishment': 'mdi-tools',
    'distribution_delivery': 'mdi-truck-delivery',
    'stem_mentoring': 'mdi-account-tie',
    'stem_workshops': 'mdi-school'
  }
  return icons[interest] || 'mdi-help-circle'
}

// Filtered volunteers based on search query and interest filter
const filteredVolunteers = computed(() => {
  let filtered = volunteers.value

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(volunteer =>
      volunteer.name.toLowerCase().includes(query) ||
      volunteer.email.toLowerCase().includes(query) ||
      (volunteer.phone && volunteer.phone.toLowerCase().includes(query))
    )
  }

  // Apply interest filter
  if (interestFilter.value) {
    filtered = filtered.filter(volunteer =>
      volunteer.interested_in?.includes(interestFilter.value)
    )
  }

  return filtered
})

// Computed property for emails list
const emailsList = computed(() => {
  return filteredVolunteers.value.map(v => v.email).join(', ')
})

const loadVolunteers = async () => {
  loading.value = true
  try {
    const response = await api.get('/volunteers')
    volunteers.value = response.data || response || []
  } catch (error) {
    console.error('Error loading volunteers:', error)
    alert('Failed to load volunteers')
    volunteers.value = []
  } finally {
    loading.value = false
  }
}

const openViewModal = (volunteer: any) => {
  viewingVolunteer.value = volunteer
  showViewModal.value = true
}

const closeViewModal = () => {
  showViewModal.value = false
  viewingVolunteer.value = null
}

const openEditModal = (volunteer: any) => {
  // Create a deep copy to avoid mutating the original
  editingVolunteer.value = JSON.parse(JSON.stringify(volunteer))
  showEditModal.value = true
}

const closeEditModal = () => {
  showEditModal.value = false
  editingVolunteer.value = null
}

const saveVolunteer = async () => {
  if (!editingVolunteer.value) return

  saving.value = true
  try {
    await api.put(`/volunteers/${editingVolunteer.value.id}`, {
      name: editingVolunteer.value.name,
      email: editingVolunteer.value.email,
      phone: editingVolunteer.value.phone,
      interested_in: editingVolunteer.value.interested_in,
      status: editingVolunteer.value.status,
      comments: editingVolunteer.value.comments
    })

    await loadVolunteers()
    closeEditModal()
    alert('Volunteer updated successfully!')
  } catch (error) {
    console.error('Error updating volunteer:', error)
    alert('Failed to update volunteer')
  } finally {
    saving.value = false
  }
}

const deactivateVolunteer = async (volunteer: any) => {
  if (!confirm(`Are you sure you want to deactivate ${volunteer.name}?`)) {
    return
  }

  try {
    await api.post(`/volunteers/${volunteer.id}/deactivate`)
    await loadVolunteers()
  } catch (error) {
    console.error('Error deactivating volunteer:', error)
    alert('Failed to deactivate volunteer')
  }
}

const activateVolunteer = async (volunteer: any) => {
  try {
    await api.post(`/volunteers/${volunteer.id}/activate`)
    await loadVolunteers()
  } catch (error) {
    console.error('Error activating volunteer:', error)
    alert('Failed to activate volunteer')
  }
}

const deleteVolunteer = async (volunteer: any) => {
  if (!confirm(`Are you sure you want to delete ${volunteer.name}? This action cannot be undone.`)) {
    return
  }

  try {
    await api.delete(`/volunteers/${volunteer.id}`)
    await loadVolunteers()
  } catch (error) {
    console.error('Error deleting volunteer:', error)
    alert('Failed to delete volunteer')
  }
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const copyEmails = async () => {
  try {
    await navigator.clipboard.writeText(emailsList.value)
    alert('Email addresses copied to clipboard!')
  } catch (error) {
    console.error('Error copying to clipboard:', error)
    alert('Failed to copy emails. Please select and copy manually.')
  }
}

const downloadCSV = async () => {
  downloadingCSV.value = true
  try {
    // Generate CSV content using filtered volunteers
    const headers = ['Name', 'Email', 'Phone', 'Interests', 'Status', 'Comments', 'Signup Date']
    const csvRows = [headers.join(',')]

    filteredVolunteers.value.forEach(volunteer => {
      const interests = volunteer.interested_in?.map(i => getInterestLabel(i)).join('; ') || ''
      const row = [
        `"${volunteer.name}"`,
        `"${volunteer.email}"`,
        `"${volunteer.phone || ''}"`,
        `"${interests}"`,
        volunteer.status,
        `"${(volunteer.comments || '').replace(/"/g, '""')}"`,
        formatDate(volunteer.created_at)
      ]
      csvRows.push(row.join(','))
    })

    const csvContent = csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', `volunteers_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    console.error('Error downloading CSV:', error)
    alert('Failed to download CSV')
  } finally {
    downloadingCSV.value = false
  }
}

onMounted(() => {
  loadVolunteers()
})
</script>

<style scoped>
.filters-container {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;
}

@media (max-width: 768px) {
  .filters-container {
    grid-template-columns: 1fr;
  }
}

.skills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.text-wrap {
  white-space: normal;
  word-wrap: break-word;
}

:deep(.v-data-table tbody tr) {
  cursor: pointer;
}
</style>
